import type { OfflineStorage } from './OfflineStorage';
import { resourceKey } from './resourceKey';

export interface ManifestFile {
  url: string;
  sizeBytes: number;
  layerId: string;
  kind: string;
}

/** Thrown when a write fails because storage is full mid-download. */
export class QuotaExceededAbort extends Error {
  constructor(public readonly filesDone: number) {
    super('Storage quota exceeded during download');
    this.name = 'QuotaExceededAbort';
  }
}

export interface DownloadOptions {
  storage: OfflineStorage;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
  maxRetries?: number;
  /** Max files fetched in parallel. Default 6. */
  concurrency?: number;
  /** Called after each file completes, with cumulative file count + bytes. */
  onProgress?: (filesDone: number, bytesDone: number) => void;
}

const isQuotaError = (e: unknown): boolean =>
  e instanceof DOMException && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED');

/**
 * Wrap a byte stream so each chunk is counted as it flows to storage. Lets the
 * progress bar move *during* a single large file instead of only on completion.
 */
function countingStream(body: ReadableStream<Uint8Array>, onChunk: (n: number) => void): ReadableStream<Uint8Array> {
  const reader = body.getReader();
  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      const { done, value } = await reader.read();
      if (done) {
        controller.close();
        return;
      }
      if (value) {
        onChunk(value.byteLength);
        controller.enqueue(value);
      }
    },
    cancel(reason) {
      return reader.cancel(reason);
    },
  });
}

async function fetchWithRetry(
  url: string,
  fetchImpl: typeof fetch,
  maxRetries: number,
  signal?: AbortSignal,
): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    try {
      const res = await fetchImpl(url, { credentials: 'include' });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (e) {
      if (signal?.aborted || (e instanceof DOMException && e.name === 'AbortError')) throw e;
      lastErr = e;
      // brief backoff before the next attempt (skip after the last attempt)
      if (attempt < maxRetries - 1) await new Promise((r) => setTimeout(r, 150 * (attempt + 1)));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('fetch failed');
}

/**
 * Download `files` into `storage` with a bounded worker pool. Resumable (skips
 * files already present), retries transient fetch failures, and aborts cleanly
 * on quota exhaustion leaving already-stored files intact. Progress is reported
 * cumulatively as each file completes (order-independent).
 */
export async function downloadFiles(files: ManifestFile[], opts: DownloadOptions): Promise<void> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const maxRetries = opts.maxRetries ?? 3;
  const concurrency = Math.max(1, opts.concurrency ?? 6);

  // Internal controller lets one worker abort the others (quota / fatal error).
  const ac = new AbortController();
  // Bridge an externally-supplied signal so it also aborts our workers.
  if (opts.signal) {
    if (opts.signal.aborted) ac.abort();
    else opts.signal.addEventListener('abort', () => ac.abort(), { once: true });
  }
  const signal = ac.signal;

  let filesDone = 0;
  let bytesDone = 0;
  let next = 0;
  let fatal: unknown = null;

  const worker = async (): Promise<void> => {
    for (;;) {
      if (fatal) return;
      if (signal.aborted) {
        // External abort (not a fatal we raised): surface AbortError.
        if (!fatal) fatal = new DOMException('aborted', 'AbortError');
        return;
      }
      const i = next++;
      if (i >= files.length) return;
      const file = files[i];
      const key = resourceKey(file.url);
      try {
        if (await opts.storage.has(key)) {
          // Already cached (resume) — count the estimated size.
          bytesDone += file.sizeBytes;
        } else {
          const res = await fetchWithRetry(file.url, fetchImpl, maxRetries, signal);
          // Count bytes as they stream to storage so the bar moves mid-file.
          // `bytesDone` is shared across concurrent workers for a smooth total.
          const counted = countingStream(res.body as ReadableStream<Uint8Array>, (n) => {
            bytesDone += n;
            opts.onProgress?.(filesDone, bytesDone);
          });
          await opts.storage.put(key, counted);
        }
        filesDone++;
        opts.onProgress?.(filesDone, bytesDone);
      } catch (e) {
        if (fatal) return; // already aborting
        fatal = isQuotaError(e) ? new QuotaExceededAbort(filesDone) : e;
        ac.abort(); // stop the other in-flight workers quickly
        return;
      }
    }
  };

  const poolSize = Math.min(concurrency, files.length || 1);
  await Promise.all(Array.from({ length: poolSize }, () => worker()));
  if (fatal) throw fatal;
}
